/**
 * EVENTSFLO-I18N-PHASE-2G-REVIEWED-PDF-EMAIL-TEMPLATES-V1
 *
 * SuperAdmin-only PDF/email template translation PREPARATION.
 *
 * Safety:
 *   - Returns null for non-SuperAdmin.
 *   - Live PDFs and emails remain English. This card never renders
 *     a PDF, never sends an email, and never includes any
 *     "Enable translated PDFs / emails" control.
 *   - Apply button is gated by the EXACT phrase
 *     `CREATE I18N TEMPLATE DRAFTS`.
 *   - production_activation is always false.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import {
  AlertTriangle,
  CheckCircle2,
  FileText,
  Info,
  Languages,
  Mail,
  Play,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";
import { useAuth } from "../../../context/AuthContext";

const APPLY_PHRASE = "CREATE I18N TEMPLATE DRAFTS";

async function authedFetch(path, token, options = {}) {
  const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  let body = null;
  try { body = await r.json(); } catch { body = null; }
  if (!r.ok) {
    const detail = body?.detail || body || { error: `HTTP ${r.status}` };
    const msg =
      typeof detail === "string"
        ? detail
        : detail.message || detail.error || `HTTP ${r.status}`;
    const err = new Error(msg);
    err.status = r.status;
    throw err;
  }
  return body;
}

function DriftBadge({ drift }) {
  if (!drift) return null;
  if (!drift.cache_exists) {
    return (
      <Badge
        className="bg-amber-500/15 text-amber-300 border border-amber-500/30"
        data-testid="i18n-templates-drift-badge"
      >
        No cache built
      </Badge>
    );
  }
  if (drift.drift_detected) {
    return (
      <Badge
        className="bg-rose-500/15 text-rose-300 border border-rose-500/30"
        data-testid="i18n-templates-drift-badge"
      >
        Drift detected
      </Badge>
    );
  }
  return (
    <Badge
      className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
      data-testid="i18n-templates-drift-badge"
    >
      Up to date
    </Badge>
  );
}

export default function I18nTemplatePreparationCard() {
  const { user, token } = useAuth();
  const isSuperAdmin = user?.role === "superadmin";

  const [audit, setAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState(null);

  const [drift, setDrift] = useState(null);
  const [driftLoading, setDriftLoading] = useState(false);

  const [dryRunResult, setDryRunResult] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [templateTypes, setTemplateTypes] = useState(["pdf", "email"]);
  const [targetLanguage, setTargetLanguage] = useState("fr");
  const [previewType, setPreviewType] = useState("pdf");
  const [preview, setPreview] = useState(null);
  const [confirmPhrase, setConfirmPhrase] = useState("");

  const loadAudit = useCallback(async () => {
    if (!isSuperAdmin || !token) return;
    setAuditLoading(true);
    setAuditError(null);
    try {
      const body = await authedFetch(`/api/admin/i18n/templates/audit`, token);
      setAudit(body);
    } catch (e) {
      setAuditError(e.message || "Audit failed");
    } finally {
      setAuditLoading(false);
    }
  }, [isSuperAdmin, token]);

  const loadDrift = useCallback(async () => {
    if (!isSuperAdmin || !token) return;
    setDriftLoading(true);
    try {
      const body = await authedFetch(`/api/admin/i18n/catalog/drift`, token);
      setDrift(body);
    } catch (e) {
      toast.error(e.message || "Drift check failed");
    } finally {
      setDriftLoading(false);
    }
  }, [isSuperAdmin, token]);

  useEffect(() => { loadAudit(); loadDrift(); }, [loadAudit, loadDrift]);

  const toggleType = (t) => {
    setTemplateTypes((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t],
    );
  };

  const runDryRun = async () => {
    setActionLoading(true);
    try {
      const body = await authedFetch(
        `/api/admin/i18n/templates/create-drafts`,
        token,
        {
          method: "POST",
          body: JSON.stringify({
            dry_run: true,
            template_types: templateTypes,
            target_languages: [targetLanguage],
          }),
        },
      );
      setDryRunResult(body);
      toast.success(
        `Dry-run: would create ${body.created_count} drafts (skip ${body.skipped_count})`,
      );
    } catch (e) {
      toast.error(e.message || "Dry-run failed");
    } finally {
      setActionLoading(false);
    }
  };

  const applyCreate = async () => {
    if (confirmPhrase !== APPLY_PHRASE) {
      toast.error("Confirmation phrase does not match.");
      return;
    }
    setActionLoading(true);
    try {
      const body = await authedFetch(
        `/api/admin/i18n/templates/create-drafts`,
        token,
        {
          method: "POST",
          body: JSON.stringify({
            dry_run: false,
            template_types: templateTypes,
            target_languages: [targetLanguage],
            confirm: APPLY_PHRASE,
          }),
        },
      );
      toast.success(
        `Created ${body.created_count} template drafts (production_activation=false)`,
      );
      setConfirmPhrase("");
      loadAudit();
      loadDrift();
    } catch (e) {
      toast.error(e.message || "Apply failed");
    } finally {
      setActionLoading(false);
    }
  };

  const loadPreview = async () => {
    setActionLoading(true);
    try {
      const body = await authedFetch(
        `/api/admin/i18n/templates/preview?template_type=${previewType}&target_language=${targetLanguage}`,
        token,
      );
      setPreview(body);
    } catch (e) {
      toast.error(e.message || "Preview failed");
    } finally {
      setActionLoading(false);
    }
  };

  const applyEnabled = useMemo(
    () => confirmPhrase === APPLY_PHRASE && !actionLoading && templateTypes.length > 0,
    [confirmPhrase, actionLoading, templateTypes],
  );

  if (!isSuperAdmin) return null;

  return (
    <Card data-testid="i18n-templates-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Languages className="w-4 h-4" /> Admin i18n template preparation
        </CardTitle>
        <p className="text-xs text-zinc-400 mt-1">
          SuperAdmin-only. Template translations are{" "}
          <strong className="text-zinc-300">preparation only</strong>.
          Live PDFs and emails remain English. Dynamic project /
          client / crew content is <strong className="text-zinc-300">not</strong>{" "}
          translated by this tool.
        </p>
      </CardHeader>
      <CardContent>
        {/* Production-unchanged banner */}
        <div
          className="rounded-md border border-amber-500/30 bg-amber-500/5 p-2 text-xs text-amber-200 flex items-start gap-2"
          data-testid="i18n-templates-production-unchanged-note"
        >
          <ShieldCheck className="w-3.5 h-3.5 mt-0.5" />
          <span>
            production_activation: <strong>false</strong>. Live PDF
            and email output remains English. No real email is sent
            by this card.
          </span>
        </div>

        {/* Catalog drift */}
        <div
          className="mt-3 rounded-md border border-zinc-700/40 p-3"
          data-testid="i18n-templates-drift-section"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-zinc-300 flex items-center gap-2">
              <Info className="w-3.5 h-3.5" /> Catalog cache drift
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={loadDrift}
              disabled={driftLoading}
              data-testid="i18n-templates-drift-refresh-btn"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
            </Button>
          </div>
          {drift ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
              <div>
                <div className="text-zinc-400">status</div>
                <div className="mt-0.5">
                  <DriftBadge drift={drift} />
                </div>
              </div>
              <div>
                <div className="text-zinc-400">approved total</div>
                <div className="text-zinc-300" data-testid="i18n-templates-drift-approved-total">
                  {drift.approved_total}
                </div>
              </div>
              <div>
                <div className="text-zinc-400">cached total</div>
                <div className="text-zinc-300" data-testid="i18n-templates-drift-cached-total">
                  {drift.cached_total}
                </div>
              </div>
              <div>
                <div className="text-zinc-400">checksums</div>
                <div className="font-mono text-zinc-300" data-testid="i18n-templates-drift-checksums">
                  approved: {drift.latest_approved_checksum?.slice(0, 8) || "—"}
                  <br />
                  cache: {drift.latest_cache_checksum?.slice(0, 8) || "—"}
                </div>
              </div>
              <div className="col-span-2 md:col-span-4 text-zinc-500" data-testid="i18n-templates-drift-message">
                {drift.message}
              </div>
            </div>
          ) : (
            <div className="text-xs text-zinc-500">Loading drift…</div>
          )}
        </div>

        {/* Audit */}
        <div
          className="mt-3 rounded-md border border-zinc-700/40 p-3"
          data-testid="i18n-templates-audit-section"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-zinc-300 flex items-center gap-2">
              <FileText className="w-3.5 h-3.5" /> Template audit
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={loadAudit}
              disabled={auditLoading}
              data-testid="i18n-templates-audit-refresh-btn"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
            </Button>
          </div>
          {auditError ? (
            <div className="text-xs text-rose-300" data-testid="i18n-templates-audit-error">
              {auditError}
            </div>
          ) : !audit ? (
            <div className="text-xs text-zinc-500">Loading audit…</div>
          ) : audit.candidate_count === 0 ? (
            <div className="text-xs text-zinc-500" data-testid="i18n-templates-audit-empty">
              No candidate template strings configured.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs mb-2">
                <div>
                  <div className="text-zinc-400">total candidates</div>
                  <div className="text-zinc-300" data-testid="i18n-templates-audit-total">
                    {audit.candidate_count}
                  </div>
                </div>
                <div>
                  <div className="text-zinc-400 flex items-center gap-1">
                    <FileText className="w-3 h-3" /> PDF
                  </div>
                  <div className="text-zinc-300" data-testid="i18n-templates-audit-pdf-count">
                    {audit.pdf_candidate_count}
                  </div>
                </div>
                <div>
                  <div className="text-zinc-400 flex items-center gap-1">
                    <Mail className="w-3 h-3" /> Email
                  </div>
                  <div className="text-zinc-300" data-testid="i18n-templates-audit-email-count">
                    {audit.email_candidate_count}
                  </div>
                </div>
              </div>
              <div
                className="overflow-auto rounded border border-zinc-700/40 max-h-64"
                data-testid="i18n-templates-audit-table"
              >
                <table className="w-full text-xs">
                  <thead className="bg-zinc-800/60 text-zinc-400 sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1">type</th>
                      <th className="text-left px-2 py-1">namespace · key</th>
                      <th className="text-left px-2 py-1">source text</th>
                      <th className="text-left px-2 py-1">target ({targetLanguage})</th>
                    </tr>
                  </thead>
                  <tbody>
                    {audit.candidates.map((c) => {
                      const ls = c.by_target_language?.[targetLanguage] || {};
                      return (
                        <tr
                          key={`${c.namespace}-${c.source_key}`}
                          className="odd:bg-zinc-900/30"
                          data-testid={`i18n-templates-audit-row-${c.namespace}-${c.source_key}`}
                        >
                          <td className="px-2 py-1 text-zinc-300">
                            <Badge
                              className={
                                c.template_type === "pdf"
                                  ? "bg-sky-500/15 text-sky-300 border border-sky-500/30"
                                  : "bg-purple-500/15 text-purple-300 border border-purple-500/30"
                              }
                            >
                              {c.template_type}
                            </Badge>
                          </td>
                          <td className="px-2 py-1 font-mono text-xs text-zinc-300">
                            <div>{c.namespace}</div>
                            <div className="text-zinc-500">{c.source_key}</div>
                          </td>
                          <td className="px-2 py-1 text-zinc-300 max-w-xs">
                            {c.source_text}
                          </td>
                          <td className="px-2 py-1">
                            {ls.approved_translation_available ? (
                              <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                                approved
                              </Badge>
                            ) : ls.already_has_draft ? (
                              <Badge className="bg-amber-500/15 text-amber-300 border border-amber-500/30">
                                draft exists
                              </Badge>
                            ) : (
                              <Badge className="bg-zinc-500/15 text-zinc-300 border border-zinc-500/30">
                                missing
                              </Badge>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Create drafts */}
        <div
          className="mt-3 rounded-md border border-zinc-700/40 p-3"
          data-testid="i18n-templates-create-section"
        >
          <div className="text-sm font-medium text-zinc-300 mb-2 flex items-center gap-2">
            <Play className="w-3.5 h-3.5" /> Create template drafts
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <div className="text-xs text-zinc-400 mb-1">template types</div>
              <div className="inline-flex gap-1">
                <Button
                  size="sm"
                  variant={templateTypes.includes("pdf") ? "default" : "outline"}
                  onClick={() => toggleType("pdf")}
                  data-testid="i18n-templates-type-pdf-btn"
                >
                  PDF
                </Button>
                <Button
                  size="sm"
                  variant={templateTypes.includes("email") ? "default" : "outline"}
                  onClick={() => toggleType("email")}
                  data-testid="i18n-templates-type-email-btn"
                >
                  Email
                </Button>
              </div>
            </div>
            <div>
              <div className="text-xs text-zinc-400 mb-1">target language</div>
              <Select value={targetLanguage} onValueChange={setTargetLanguage}>
                <SelectTrigger
                  className="w-28"
                  data-testid="i18n-templates-target-language-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fr">fr</SelectItem>
                  <SelectItem value="it">it</SelectItem>
                  <SelectItem value="es">es</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="outline"
              onClick={runDryRun}
              disabled={actionLoading || templateTypes.length === 0}
              data-testid="i18n-templates-create-dry-run-btn"
            >
              <Play className="w-3.5 h-3.5 mr-1" /> Dry-run
            </Button>
          </div>
          {dryRunResult ? (
            <div
              className="mt-2 rounded border border-zinc-700/40 bg-zinc-900/40 p-2 text-xs grid grid-cols-2 md:grid-cols-4 gap-2"
              data-testid="i18n-templates-create-dry-run-result"
            >
              <div>
                <div className="text-zinc-400">would create</div>
                <div className="text-zinc-300">{dryRunResult.created_count}</div>
              </div>
              <div>
                <div className="text-zinc-400">would skip</div>
                <div className="text-zinc-300">{dryRunResult.skipped_count}</div>
              </div>
              <div>
                <div className="text-zinc-400">dry_run</div>
                <div className="text-zinc-300">{String(dryRunResult.dry_run)}</div>
              </div>
              <div>
                <div className="text-zinc-400">activation</div>
                <div className="text-zinc-300">
                  {String(dryRunResult.production_activation)}
                </div>
              </div>
            </div>
          ) : null}

          <div className="mt-3">
            <label
              htmlFor="i18n-templates-confirm-input"
              className="text-xs text-zinc-400 block mb-1"
            >
              Type exactly to enable Apply:
              <span className="font-mono text-amber-300 ml-1">
                {APPLY_PHRASE}
              </span>
            </label>
            <div className="flex flex-wrap gap-2 items-center">
              <Input
                id="i18n-templates-confirm-input"
                value={confirmPhrase}
                onChange={(e) => setConfirmPhrase(e.target.value)}
                placeholder={APPLY_PHRASE}
                className="max-w-md font-mono text-xs"
                data-testid="i18n-templates-confirm-input"
              />
              <Button
                onClick={applyCreate}
                disabled={!applyEnabled}
                data-testid="i18n-templates-create-apply-btn"
              >
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Apply create
              </Button>
              {!applyEnabled ? (
                <div
                  className="text-xs text-zinc-400 flex items-center gap-1"
                  data-testid="i18n-templates-confirm-helper"
                >
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
                  Exact phrase required.
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* Preview approved templates */}
        <div
          className="mt-3 rounded-md border border-zinc-700/40 p-3"
          data-testid="i18n-templates-preview-section"
        >
          <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
            <div className="text-sm font-medium text-zinc-300 flex items-center gap-2">
              <FileText className="w-3.5 h-3.5" /> Preview approved templates
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={previewType} onValueChange={setPreviewType}>
                <SelectTrigger
                  className="w-28"
                  data-testid="i18n-templates-preview-type-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pdf">PDF</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                </SelectContent>
              </Select>
              <Button
                size="sm"
                variant="outline"
                onClick={loadPreview}
                disabled={actionLoading}
                data-testid="i18n-templates-preview-btn"
              >
                <Play className="w-3.5 h-3.5 mr-1" /> Preview
              </Button>
            </div>
          </div>
          {preview ? (
            <div className="text-xs space-y-2" data-testid="i18n-templates-preview-result">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div>
                  <div className="text-zinc-400">type</div>
                  <div className="text-zinc-300">{preview.template_type}</div>
                </div>
                <div>
                  <div className="text-zinc-400">language</div>
                  <div className="text-zinc-300">{preview.target_language}</div>
                </div>
                <div>
                  <div className="text-zinc-400">approved</div>
                  <div
                    className="text-zinc-300"
                    data-testid="i18n-templates-preview-approved-count"
                  >
                    {preview.approved_count}
                  </div>
                </div>
                <div>
                  <div className="text-zinc-400">missing</div>
                  <div
                    className="text-zinc-300"
                    data-testid="i18n-templates-preview-missing-count"
                  >
                    {preview.missing_count}
                  </div>
                </div>
              </div>
              {preview.approved_count > 0 ? (
                <div className="overflow-auto rounded border border-zinc-700/40 max-h-48">
                  <table className="w-full text-xs">
                    <thead className="bg-zinc-800/60 text-zinc-400">
                      <tr>
                        <th className="text-left px-2 py-1">key</th>
                        <th className="text-left px-2 py-1">English</th>
                        <th className="text-left px-2 py-1">approved</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.preview_rows.map((r) => (
                        <tr
                          key={`${r.namespace}-${r.source_key}`}
                          className="odd:bg-zinc-900/30"
                          data-testid={`i18n-templates-preview-row-${r.namespace}-${r.source_key}`}
                        >
                          <td className="px-2 py-1 font-mono text-zinc-300">
                            {r.namespace}.{r.source_key}
                          </td>
                          <td className="px-2 py-1 text-zinc-300">{r.source_text}</td>
                          <td className="px-2 py-1 text-emerald-300">
                            {r.approved_translation}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-zinc-500" data-testid="i18n-templates-preview-empty">
                  No approved translations yet for{" "}
                  {preview.template_type}/{preview.target_language}.
                </div>
              )}
            </div>
          ) : (
            <div className="text-xs text-zinc-500">
              Pick a template type and language, then click Preview.
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
